import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MedicalSpecialty } from '@pixacare/domain';
import { MedicalSpecialtiesHttpService } from '@services/http/medical-specialties.http.service';
import { GetFromIdPipe } from '@shared/pipes/get-from-id.pipe';
import { TuiDropdownContent, TuiFilterByInputPipe, TuiInput, TuiLabel } from '@taiga-ui/core';
import { TuiChevron, TuiComboBox, TuiDataListWrapper, TuiStringifyContentPipe } from '@taiga-ui/kit';

@Component({
  selector: 'pxc-medical-specialty-picker',
  imports: [
    CommonModule,
    FormsModule,
    TranslocoModule,
    TuiChevron,
    TuiComboBox,
    TuiDataListWrapper,
    TuiFilterByInputPipe,
    TuiLabel,
    TuiStringifyContentPipe,
    TuiInput,
    TuiDropdownContent,
    GetFromIdPipe,
  ],
  templateUrl: './medical-specialty-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: MedicalSpecialtyPickerComponent,
    },
  ],
})
export class MedicalSpecialtyPickerComponent implements ControlValueAccessor {
  @Input() showLabel = true;

  private readonly medicalSpecialtiesHttpService = inject(MedicalSpecialtiesHttpService);
  private readonly translocoService = inject(TranslocoService);

  readonly items$ = this.medicalSpecialtiesHttpService.getMedicalSpecialties();

  disabled = false;
  touched = false;
  value: number = null;

  onChange: (value: number) => void = () => {};
  onTouched: () => void = () => {};

  onValueChange(value: MedicalSpecialty): void {
    if (!value) {
      return;
    }

    this.markAsTouched();
    const newValue = value?.id ?? null;
    if (this.value !== newValue) {
      this.value = newValue;
      this.onChange(this.value);
    }
  }

  writeValue(value: number): void {
    this.value = value;
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  stringify = (item: MedicalSpecialty): string => {
    return this.translocoService.translate(item.label);
  };
}
